import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Activity,
  ArrowLeft,
  MapPin,
  Star,
  Video,
  Home,
  Building,
  Clock,
  Loader2,
  CheckCircle,
  Calendar as CalendarIcon,
  AlertTriangle,
  Sunrise,
  Sun,
  Moon,
} from 'lucide-react';
import { apiRequest, getInitials, formatDate } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format, addDays, isBefore, startOfToday } from 'date-fns';
import { analytics } from '../lib/analytics';

const PHYSIGN_LOGO = "https://customer-assets.emergentagent.com/job_4bf5f59f-ff2a-4b89-ab5f-09ffb3b85c36/artifacts/91dqnx5f_high-resolution-color-logo-3.png";
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// Fallback static slots when a provider has NOT configured availability via the wizard
const DEFAULT_TIME_SLOTS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '14:00', '14:30', '15:00', '15:30', '16:00',
  '16:30', '17:00', '17:30', '18:00', '18:30', '19:00',
  '19:30', '20:00', '20:30', '21:00', '21:30', '22:00',
];

// Given a "HH:MM" time and a 30-min step, generate slots between two "HH:MM" times.
const generateStepSlots = (fromHHMM, toHHMM, stepMin = 30) => {
  const out = [];
  const [fh, fm] = String(fromHHMM || '09:00').split(':').map((n) => parseInt(n, 10) || 0);
  const [th, tm] = String(toHHMM || '17:00').split(':').map((n) => parseInt(n, 10) || 0);
  let cur = fh * 60 + fm;
  const end = th * 60 + tm;
  while (cur < end) {
    const h = String(Math.floor(cur / 60)).padStart(2, '0');
    const m = String(cur % 60).padStart(2, '0');
    out.push(`${h}:${m}`);
    cur += stepMin;
  }
  return out;
};

// Compute the concrete list of time slots for a given date, based on the provider's
// wizard availability (weekly + leaves + holidays + date_overrides). Falls back to
// DEFAULT_TIME_SLOTS if the provider hasn't configured availability yet.
const computeSlotsForDate = (dateObj, availability) => {
  if (!dateObj) return [];
  const dateISO = format(dateObj, 'yyyy-MM-dd');

  // No availability object -> legacy fallback (all default slots visible)
  if (!availability || typeof availability !== 'object' || !availability.weekly) {
    return DEFAULT_TIME_SLOTS.slice();
  }

  // Check leaves (full-day off) & holidays
  const isOnLeave = (availability.leaves || []).some((l) => {
    if (!l) return false;
    const from = l.from || l.date;
    const to = l.to || l.date;
    if (!from) return false;
    return dateISO >= from && dateISO <= (to || from);
  });
  if (isOnLeave) return [];

  const isHoliday = (availability.holidays || []).some((h) => (h?.date || h) === dateISO);
  if (isHoliday) return [];

  // Check date overrides (specific-date custom hours or closed)
  const override = (availability.overrides || {})[dateISO] || (availability.date_overrides || {})[dateISO];
  if (override) {
    if (override.closed || override.is_closed) return [];
    if (Array.isArray(override.slots) && override.slots.length) {
      return override.slots.flatMap((s) => generateStepSlots(s.from || s.start, s.to || s.end));
    }
  }

  // Weekly schedule for this day
  const dayKey = DAY_KEYS[dateObj.getDay()];
  const dayCfg = availability.weekly?.days?.[dayKey] || availability.weekly?.[dayKey];
  if (!dayCfg || !dayCfg.enabled) return [];

  const slotsList = (dayCfg.slots || []).flatMap((s) =>
    generateStepSlots(s.from || s.start, s.to || s.end)
  );

  // Subtract breaks (lunch/tea)
  const breaks = (dayCfg.breaks || []).map((b) => ({
    from: b.from || b.start,
    to: b.to || b.end,
  }));
  const isInBreak = (hhmm) => {
    const [h, m] = hhmm.split(':').map((n) => parseInt(n, 10));
    const mins = h * 60 + m;
    return breaks.some((b) => {
      if (!b.from || !b.to) return false;
      const [fh, fm] = b.from.split(':').map((n) => parseInt(n, 10));
      const [th, tm] = b.to.split(':').map((n) => parseInt(n, 10));
      return mins >= fh * 60 + fm && mins < th * 60 + tm;
    });
  };
  return slotsList.filter((t) => !isInBreak(t));
};

// Compute booking window (max date the calendar allows).
// If provider set only limited days in `weekly` OR a max_advance_days is configured,
// clamp the calendar. Default: 30 days out.
const computeMaxBookingDate = (availability) => {
  const days = 30; // sensible default
  const max = new Date();
  max.setDate(max.getDate() + days);
  // Optional override: if availability.max_advance_days is set explicitly
  if (availability && typeof availability.max_advance_days === 'number' && availability.max_advance_days > 0) {
    const custom = new Date();
    custom.setDate(custom.getDate() + Math.min(availability.max_advance_days, 90));
    return custom;
  }
  return max;
};

// Convert 24h "HH:MM" to 12h with AM/PM (e.g., "14:30" -> "2:30 PM")
const to12h = (t) => {
  const [hStr, m] = t.split(':');
  const h = parseInt(hStr, 10);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m} ${period}`;
};

// Group slots into Morning / Afternoon / Evening based on hour
const groupTimeSlots = (slots) => {
  const groups = { morning: [], afternoon: [], evening: [] };
  slots.forEach((t) => {
    const h = parseInt(t.split(':')[0], 10);
    if (h < 12) groups.morning.push(t);
    else if (h < 17) groups.afternoon.push(t);
    else groups.evening.push(t);
  });
  return groups;
};

export default function BookAppointment() {
  const navigate = useNavigate();
  const { providerId } = useParams();
  const [searchParams] = useSearchParams();

  // If URL contains ?day=monday, pre-select the next occurrence of that weekday.
  const initialSelectedDate = () => {
    const requested = (searchParams.get('day') || '').toLowerCase();
    if (!requested) return new Date();
    const idx = DAY_KEYS.indexOf(requested);
    if (idx < 0) return new Date();
    const today = new Date();
    const currentIdx = today.getDay(); // 0=Sun..6=Sat matches DAY_KEYS order
    const delta = ((idx - currentIdx) + 7) % 7 || 7; // if today, jump to next week's occurrence
    const target = new Date(today);
    target.setDate(today.getDate() + delta);
    return target;
  };
  const { isAuthenticated, user } = useAuth();
  
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [step, setStep] = useState(1); // 1: service, 2: date/time, 3: details, 4: confirm
  
  const [selectedService, setSelectedService] = useState('');
  const [selectedDate, setSelectedDate] = useState(initialSelectedDate);
  const [selectedTime, setSelectedTime] = useState('');
  const [symptoms, setSymptoms] = useState('');
  const [notes, setNotes] = useState('');
  const [address, setAddress] = useState('');
  const [nurseServices, setNurseServices] = useState([]);

  const isHomeService = selectedService === 'home_visit' || (provider?.user?.role === 'nurse' && nurseServices.length > 0) || ['night_duty', 'full_time', 'part_time'].includes(selectedService);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    fetchProvider();
    fetchPatientAddress();
  }, [providerId, isAuthenticated]);

  // Reset selectedTime if it becomes a past slot when date changes (e.g. switched to today)
  useEffect(() => {
    if (!selectedDate || !selectedTime) return;
    const now = new Date();
    const isToday = format(selectedDate, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd');
    if (!isToday) return;
    const [h, m] = selectedTime.split(':').map(Number);
    const slotDate = new Date(selectedDate);
    slotDate.setHours(h, m, 0, 0);
    if (slotDate <= now) setSelectedTime('');
  }, [selectedDate, selectedTime]);

  const fetchPatientAddress = async () => {
    try {
      const data = await apiRequest('/patient/profile');
      if (data?.address) {
        setAddress(data.address);
      }
    } catch { /* no saved address */ }
  };

  const fetchProvider = async () => {
    try {
      const data = await apiRequest(`/providers/${providerId}`);
      setProvider(data);
      
      // Parse nurse services from bio for nurses
      let parsedNurseServices = [];
      if (data.user?.role === 'nurse' && data.profile?.bio) {
        try {
          const parsed = JSON.parse(data.profile.bio);
          if (parsed.nurse_data?.nurse_services) {
            parsedNurseServices = parsed.nurse_data.nurse_services.filter(s => s.name && s.price && s.pricing_type);
          }
        } catch { /* not JSON */ }
      }
      setNurseServices(parsedNurseServices);
      
      // Set default service
      if (data.user?.role === 'nurse' && parsedNurseServices.length > 0) {
        setSelectedService(parsedNurseServices[0].name);
      } else if (data.profile?.services?.length > 0) {
        const nonOnline = data.profile.services.filter(s => s !== 'online');
        if (nonOnline.length > 0) setSelectedService(nonOnline[0]);
        else setSelectedService('clinic');
      } else {
        setSelectedService('clinic');
      }
    } catch (error) {
      toast.error('Provider not found');
      navigate('/search');
    } finally {
      setLoading(false);
    }
  };

  const handleBook = async () => {
    if (!selectedDate || !selectedTime || !selectedService) {
      toast.error('Please select all required fields');
      return;
    }
    if (provider?.user?.is_active === false) {
      toast.error('This provider is currently unavailable');
      return;
    }

    setBooking(true);
    try {
      const isNurse = provider?.user?.role === 'nurse' && nurseServices.length > 0;
      const selectedNurseSvc = isNurse ? nurseServices.find(s => s.name === selectedService) : null;
      
      const aptResponse = await apiRequest('/appointments', {
        method: 'POST',
        body: JSON.stringify({
          provider_id: providerId,
          provider_type: provider?.user?.role || 'doctor',
          service_type: isNurse ? 'home_visit' : selectedService,
          appointment_date: format(selectedDate, 'yyyy-MM-dd'),
          time_slot: selectedTime,
          symptoms: isNurse ? `Service: ${selectedService}` + (symptoms ? ` | ${symptoms}` : '') : symptoms,
          notes,
          address: isHomeService ? address : '',
          fee: selectedNurseSvc ? parseFloat(selectedNurseSvc.price) : undefined,
        }),
      });
      
      // Track booking as a purchase event in GA4
      const fee = selectedNurseSvc ? parseFloat(selectedNurseSvc.price) : (
        selectedService === 'home_visit' 
          ? (provider?.profile?.home_visit_fee || provider?.profile?.consultation_fee || 0)
          : (provider?.profile?.consultation_fee || 0)
      );
      analytics.bookingCompleted(
        aptResponse?.id || aptResponse?.appointment?.id || 'unknown',
        fee,
        isNurse ? 'home_visit' : selectedService
      );
      
      toast.success('Appointment booked successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBooking(false);
    }
  };

  const getServiceLabel = (service) => {
    const labels = {
      home_visit: 'Home Visit',
      clinic: 'Clinic Visit',
      night_duty: 'Night Duty',
      full_time: 'Full Time',
      part_time: 'Part Time',
    };
    return labels[service] || service;
  };

  const getServiceIcon = (service) => {
    const icons = {
      home_visit: Home,
      clinic: Building,
      night_duty: Clock,
      full_time: Activity,
      part_time: Clock,
    };
    const Icon = icons[service] || Building;
    return <Icon className="w-5 h-5" />;
  };

  const getFee = () => {
    // For nurses, use nurse service specific price
    if (provider?.user?.role === 'nurse' && nurseServices.length > 0) {
      const svc = nurseServices.find(s => s.name === selectedService);
      return svc ? parseFloat(svc.price) : provider?.profile?.home_visit_fee || 1000;
    }
    if (['home_visit', 'night_duty', 'full_time', 'part_time'].includes(selectedService)) {
      return provider?.profile?.home_visit_fee || 1000;
    }
    return provider?.profile?.consultation_fee || 500;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F62FE]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="book-appointment-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-center h-16 gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Link to="/" className="flex items-center gap-2">
              <span className="text-lg font-bold text-[#3B1C73]" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Physign
              </span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-2">
            {[1, 2, 3, 4].map((s) => (
              <React.Fragment key={s}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    step >= s
                      ? 'bg-[#0F62FE] text-white'
                      : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                </div>
                {s < 4 && (
                  <div
                    className={`w-12 sm:w-20 h-1 rounded ${
                      step > s ? 'bg-[#0F62FE]' : 'bg-slate-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs sm:text-sm text-slate-600 px-2">
            <span>Service</span>
            <span>Date & Time</span>
            <span>Details</span>
            <span>Confirm</span>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Unavailable Warning */}
          {provider?.user?.is_active === false && (
            <div className="md:col-span-3 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3" data-testid="unavailable-warning">
              <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-800">Provider Currently Unavailable</h3>
                <p className="text-sm text-red-600 mt-1">This provider is currently not accepting new appointments. Please try again later or choose another provider.</p>
                <Button variant="outline" className="mt-3 border-red-200 text-red-700 hover:bg-red-100" onClick={() => navigate('/search')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />Find Another Provider
                </Button>
              </div>
            </div>
          )}

          {/* Provider Summary Card */}
          <Card className="md:col-span-1 h-fit">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-16 h-16 rounded-full bg-[#E0F2FE] flex items-center justify-center text-xl font-bold text-[#0F62FE]">
                  {getInitials(provider?.user?.name || 'Dr')}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">
                    {provider?.user?.name || 'Healthcare Provider'}
                  </h3>
                  <p className="text-sm text-[#0F62FE] capitalize">
                    {provider?.profile?.specialization || provider?.user?.role}
                  </p>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <Star className="w-4 h-4 text-amber-400" />
                  <span>{provider?.profile?.rating || '4.5'} ({provider?.profile?.total_reviews || 0} reviews)</span>
                </div>
                {provider?.profile?.location && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="w-4 h-4" />
                    <span>{provider?.profile?.location}</span>
                  </div>
                )}
              </div>

              {selectedService && selectedDate && selectedTime && (
                <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
                  <h4 className="font-medium text-slate-900">Booking Summary</h4>
                  <div className="text-sm text-slate-600 space-y-1">
                    <p className="flex items-center gap-2">
                      {getServiceIcon(selectedService)}
                      {getServiceLabel(selectedService)}
                    </p>
                    <p className="flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4" />
                      {format(selectedDate, 'MMM dd, yyyy')}
                    </p>
                    <p className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {to12h(selectedTime)}
                    </p>
                    {isHomeService && address && (
                      <p className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                        <span className="text-xs">{address}</span>
                      </p>
                    )}
                  </div>
                  <div className="pt-2">
                    <p className="text-lg font-bold text-slate-900">₹{getFee()}</p>
                    <p className="text-xs text-slate-500">{provider?.user?.role === 'nurse' && nurseServices.length > 0 ? selectedService || 'Select service' : 'Consultation fee'}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Booking Steps */}
          <Card className="md:col-span-2">
            <CardContent className="p-6">
              {/* Step 1: Service Selection */}
              {step === 1 && (
                <div className="space-y-6" data-testid="step-service">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900 mb-2">Select Service</h2>
                    <p className="text-slate-600">Choose the service you need</p>
                  </div>

                  {/* Nurse-specific services from NurseServicesForm */}
                  {provider?.user?.role === 'nurse' && nurseServices.length > 0 ? (
                    <RadioGroup value={selectedService} onValueChange={setSelectedService}>
                      <div className="grid gap-3">
                        {nurseServices.map((svc) => (
                          <Label
                            key={svc.name}
                            htmlFor={`nurse-svc-${svc.name}`}
                            className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              selectedService === svc.name
                                ? 'border-[#0F62FE] bg-blue-50'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <RadioGroupItem value={svc.name} id={`nurse-svc-${svc.name}`} className="sr-only" />
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                              selectedService === svc.name ? 'bg-[#0F62FE] text-white' : 'bg-slate-100 text-slate-600'
                            }`}>
                              <Home className="w-5 h-5" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">{svc.name}</p>
                              <p className="text-sm text-slate-500">{svc.pricing_type}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-slate-900">₹{svc.price}</p>
                              <p className="text-xs text-slate-500">{svc.pricing_type}</p>
                            </div>
                          </Label>
                        ))}
                      </div>
                    </RadioGroup>
                  ) : (
                    /* Doctor/generic services */
                    <RadioGroup value={selectedService} onValueChange={setSelectedService}>
                      <div className="grid gap-3">
                        {(provider?.profile?.services?.length > 0 
                          ? provider.profile.services.filter(s => s !== 'online')
                          : ['clinic', 'home_visit']
                        ).map((service) => (
                          <Label
                            key={service}
                            htmlFor={service}
                            className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              selectedService === service
                                ? 'border-[#0F62FE] bg-blue-50'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <RadioGroupItem value={service} id={service} className="sr-only" />
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                              selectedService === service ? 'bg-[#0F62FE] text-white' : 'bg-slate-100 text-slate-600'
                            }`}>
                              {getServiceIcon(service)}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">{getServiceLabel(service)}</p>
                              <p className="text-sm text-slate-500">
                                {service === 'home_visit' && 'Provider visits your home'}
                                {service === 'clinic' && 'Visit the clinic'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-slate-900">
                                ₹{service === 'home_visit' ? provider?.profile?.home_visit_fee || 1000 : provider?.profile?.consultation_fee || 500}
                              </p>
                            </div>
                          </Label>
                        ))}
                      </div>
                    </RadioGroup>
                  )}

                  <Button
                    className="w-full bg-[#0F62FE] hover:bg-[#0050E6] text-white h-12"
                    disabled={!selectedService}
                    onClick={() => setStep(2)}
                    data-testid="next-step-btn"
                  >
                    Continue
                  </Button>
                </div>
              )}

              {/* Step 2: Date & Time Selection */}
              {step === 2 && (
                <div className="space-y-6" data-testid="step-datetime">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900 mb-2">Select Date & Time</h2>
                    <p className="text-slate-600">Choose your preferred appointment slot</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="mb-2 block">Select Date</Label>
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={(date) => {
                          if (isBefore(date, startOfToday())) return true;
                          // Block dates the provider is not available on (weekly disabled, leave, holiday)
                          const dayHasSlots = computeSlotsForDate(date, provider?.profile?.availability).length > 0;
                          return !dayHasSlots;
                        }}
                        toDate={computeMaxBookingDate(provider?.profile?.availability)}
                        className="rounded-xl border"
                        data-testid="booking-calendar"
                      />
                    </div>

                    <div>
                      <Label className="mb-3 block">Select Time</Label>
                      {(() => {
                        const doctorSlots = computeSlotsForDate(selectedDate, provider?.profile?.availability);
                        const groups = groupTimeSlots(doctorSlots);
                        const noSlotsForDay = doctorSlots.length === 0;
                        const sections = [
                          { key: 'morning', label: 'Morning', sub: '9 AM – 12 PM', icon: Sunrise, iconBg: 'bg-amber-50', iconColor: 'text-amber-600', slots: groups.morning },
                          { key: 'afternoon', label: 'Afternoon', sub: '12 PM – 5 PM', icon: Sun, iconBg: 'bg-orange-50', iconColor: 'text-orange-600', slots: groups.afternoon },
                          { key: 'evening', label: 'Evening', sub: '5 PM – 10 PM', icon: Moon, iconBg: 'bg-indigo-50', iconColor: 'text-indigo-600', slots: groups.evening },
                        ];

                        // Disable past time slots when selected date is today
                        const now = new Date();
                        const isToday = selectedDate && format(selectedDate, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd');
                        const isPastSlot = (time) => {
                          if (!isToday) return false;
                          const [h, m] = time.split(':').map(Number);
                          const slotDate = new Date(selectedDate);
                          slotDate.setHours(h, m, 0, 0);
                          return slotDate <= now;
                        };

                        return (
                          <div className="space-y-4" data-testid="time-slot-sections">
                            {noSlotsForDay && (
                              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="no-slots-message">
                                <p className="font-medium">No slots available on {selectedDate ? format(selectedDate, 'EEE, MMM d') : 'this date'}</p>
                                <p className="text-xs mt-1">The doctor is not available on this day. Please pick another date from the calendar.</p>
                              </div>
                            )}
                            {!noSlotsForDay && sections.map(({ key, label, sub, icon: Icon, iconBg, iconColor, slots }) => {
                              const allPast = slots.every(isPastSlot);
                              return (
                                <div key={key} data-testid={`time-section-${key}`} className={allPast ? 'opacity-50' : ''}>
                                  <div className="flex items-center gap-2 mb-2">
                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${iconBg}`}>
                                      <Icon className={`w-4 h-4 ${iconColor}`} />
                                    </div>
                                    <div>
                                      <p className="text-sm font-semibold text-slate-800">{label}</p>
                                      <p className="text-[10px] text-slate-400 -mt-0.5">{sub}{allPast && ' • All slots passed'}</p>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                    {slots.map((time) => {
                                      const past = isPastSlot(time);
                                      return (
                                        <button
                                          key={time}
                                          type="button"
                                          disabled={past}
                                          className={`time-slot ${selectedTime === time ? 'selected' : ''} ${past ? 'opacity-40 cursor-not-allowed line-through' : ''}`}
                                          onClick={() => !past && setSelectedTime(time)}
                                          aria-disabled={past}
                                          data-testid={`time-${time}`}
                                        >
                                          {to12h(time)}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1 h-12"
                      onClick={() => setStep(1)}
                    >
                      Back
                    </Button>
                    <Button
                      className="flex-1 bg-[#0F62FE] hover:bg-[#0050E6] text-white h-12"
                      disabled={!selectedDate || !selectedTime}
                      onClick={() => setStep(3)}
                      data-testid="next-step-btn"
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Additional Details */}
              {step === 3 && (
                <div className="space-y-6" data-testid="step-details">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900 mb-2">Additional Details</h2>
                    <p className="text-slate-600">Help the doctor understand your needs better</p>
                  </div>

                  <div className="space-y-4">
                    {isHomeService && (
                      <div>
                        <Label htmlFor="address" className="flex items-center gap-1.5">
                          <MapPin className="w-4 h-4 text-red-500" />
                          Home Address <span className="text-red-500">*</span>
                        </Label>
                        <Textarea
                          id="address"
                          placeholder="Enter your complete home address (House no., Street, Landmark, City, Pincode)"
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          className="mt-1 border-red-200 focus:border-red-400"
                          rows={3}
                          required
                          data-testid="address-input"
                        />
                        <p className="text-xs text-slate-500 mt-1">Provider will visit this address</p>
                      </div>
                    )}

                    <div>
                      <Label htmlFor="symptoms">Symptoms (Optional)</Label>
                      <Textarea
                        id="symptoms"
                        placeholder="Describe your symptoms..."
                        value={symptoms}
                        onChange={(e) => setSymptoms(e.target.value)}
                        className="mt-1"
                        rows={3}
                        data-testid="symptoms-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="notes">Additional Notes (Optional)</Label>
                      <Textarea
                        id="notes"
                        placeholder="Any other information you'd like to share..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="mt-1"
                        rows={3}
                        data-testid="notes-input"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1 h-12"
                      onClick={() => setStep(2)}
                    >
                      Back
                    </Button>
                    <Button
                      className="flex-1 bg-[#0F62FE] hover:bg-[#0050E6] text-white h-12"
                      onClick={() => setStep(4)}
                      disabled={isHomeService && !address.trim()}
                      data-testid="next-step-btn"
                    >
                      Review Booking
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 4: Confirmation */}
              {step === 4 && (
                <div className="space-y-6" data-testid="step-confirm">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900 mb-2">Confirm Booking</h2>
                    <p className="text-slate-600">Review your appointment details</p>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">Service</p>
                        <p className="font-medium">{provider?.user?.role === 'nurse' && nurseServices.length > 0 ? selectedService : getServiceLabel(selectedService)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Date</p>
                        <p className="font-medium">{selectedDate && format(selectedDate, 'MMM dd, yyyy')}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Time</p>
                        <p className="font-medium">{to12h(selectedTime)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Fee</p>
                        <p className="font-medium">₹{getFee()}</p>
                      </div>
                    </div>

                    {symptoms && (
                      <div>
                        <p className="text-sm text-slate-500">Symptoms</p>
                        <p className="text-slate-700">{symptoms}</p>
                      </div>
                    )}

                    {isHomeService && address && (
                      <div className="bg-white p-3 rounded-lg border border-red-100">
                        <p className="text-sm text-slate-500 flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-red-500" />
                          Visit Address
                        </p>
                        <p className="text-slate-700 mt-0.5">{address}</p>
                      </div>
                    )}

                    {notes && (
                      <div>
                        <p className="text-sm text-slate-500">Notes</p>
                        <p className="text-slate-700">{notes}</p>
                      </div>
                    )}
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> Payment will be collected at the time of consultation. 
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1 h-12"
                      onClick={() => setStep(3)}
                    >
                      Back
                    </Button>
                    <Button
                      className="flex-1 bg-[#0F62FE] hover:bg-[#0050E6] text-white h-12"
                      onClick={handleBook}
                      disabled={booking}
                      data-testid="confirm-booking-btn"
                    >
                      {booking ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Booking...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Confirm Booking
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
